import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { StaffRepository } from '../api/staff-repository'
import { ClubRolesRepository } from '../api/club-roles-repository'
import { assertPresent } from '@/shared/utils/assert'

export const staffKeys = {
  all: (clubId: string) => ['staff', clubId, 'all'] as const,
  byUid: (clubId: string, uid: string) => ['staff', clubId, 'uid', uid] as const,
  roles: (clubId: string) => ['club-roles', clubId] as const,
}

export function useStaff(clubId: string | null) {
  const repo = useMemo(() => (clubId ? new StaffRepository(clubId) : null), [clubId])
  return useQuery({
    queryKey: clubId ? staffKeys.all(clubId) : ['staff', 'none'],
    queryFn: () => assertPresent(repo, 'repo').listAll(),
    enabled: !!repo,
    staleTime: 60_000,
  })
}

export function useStaffByUid(clubId: string | null, uid: string | null) {
  const repo = useMemo(() => (clubId ? new StaffRepository(clubId) : null), [clubId])
  return useQuery({
    queryKey: clubId && uid ? staffKeys.byUid(clubId, uid) : ['staff', 'none', 'uid'],
    queryFn: () =>
      assertPresent(repo, 'repo').findByUid(assertPresent(uid, 'uid')),
    enabled: !!repo && !!uid,
    staleTime: 5 * 60_000,
  })
}

export function useClubRoles(clubId: string | null) {
  const repo = useMemo(() => (clubId ? new ClubRolesRepository(clubId) : null), [clubId])
  return useQuery({
    queryKey: clubId ? staffKeys.roles(clubId) : ['club-roles', 'none'],
    queryFn: () => assertPresent(repo, 'repo').listAll(),
    enabled: !!repo,
    staleTime: 5 * 60_000,
  })
}
